import { useEffect, useState } from 'react';
import axios from 'axios';
import { Settings, Save, Mail, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SystemConfig = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState({ smtp: [], payment: [], general: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/system-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
      
      // Initialize form data
      const initialData = {};
      Object.values(response.data).flat().forEach(item => {
        initialData[item.config_key] = item.config_value;
      });
      setFormData(initialData);
    } catch (error) {
      toast.error('Failed to fetch configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/system-config`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Configuration saved successfully!');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* SMTP Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            SMTP Email Settings
          </CardTitle>
          <CardDescription>
            Configure your email server for automated notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>SMTP Host</Label>
              <Input
                value={formData.smtp_host || ''}
                onChange={(e) => handleChange('smtp_host', e.target.value)}
                placeholder="smtp.gmail.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>SMTP Port</Label>
              <Input
                value={formData.smtp_port || ''}
                onChange={(e) => handleChange('smtp_port', e.target.value)}
                placeholder="587"
                type="number"
                className="mt-1"
              />
            </div>
            <div>
              <Label>SMTP Username</Label>
              <Input
                value={formData.smtp_username || ''}
                onChange={(e) => handleChange('smtp_username', e.target.value)}
                placeholder="your-email@gmail.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>SMTP Password</Label>
              <Input
                value={formData.smtp_password || ''}
                onChange={(e) => handleChange('smtp_password', e.target.value)}
                type="password"
                placeholder="App password"
                className="mt-1"
              />
            </div>
            <div>
              <Label>From Email</Label>
              <Input
                value={formData.smtp_from_email || ''}
                onChange={(e) => handleChange('smtp_from_email', e.target.value)}
                placeholder="noreply@pactac.shop"
                className="mt-1"
              />
            </div>
            <div>
              <Label>From Name</Label>
              <Input
                value={formData.smtp_from_name || ''}
                onChange={(e) => handleChange('smtp_from_name', e.target.value)}
                placeholder="Pactac Store"
                className="mt-1"
              />
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 p-4 rounded text-sm">
            <p className="font-medium mb-1">Gmail Setup:</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-700">
              <li>Go to Google Account → Security</li>
              <li>Enable 2-Factor Authentication</li>
              <li>Generate an "App Password"</li>
              <li>Use that password here</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Payment Gateways */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Gateways
          </CardTitle>
          <CardDescription>
            Enable or disable payment methods
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {config.payment?.map((item) => (
              <div key={item.config_key} className="flex items-center justify-between p-4 border rounded">
                <div>
                  <p className="font-medium capitalize">{item.config_key.replace('_enabled', '').replace('_', ' ')}</p>
                  <p className="text-sm text-gray-600">Status: {formData[item.config_key] === '1' ? 'Enabled' : 'Disabled'}</p>
                </div>
                <Switch
                  checked={formData[item.config_key] === '1'}
                  onCheckedChange={(checked) => handleChange(item.config_key, checked ? '1' : '0')}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Automation Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Automation Settings
          </CardTitle>
          <CardDescription>
            Configure automated shipping workflow
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Auto Shipping Delay (Hours)</Label>
              <Input
                value={formData.auto_shipping_delay_hours || ''}
                onChange={(e) => handleChange('auto_shipping_delay_hours', e.target.value)}
                type="number"
                placeholder="36"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Orders ship automatically after this many hours</p>
            </div>
            <div>
              <Label>Estimated Delivery (Days)</Label>
              <Input
                value={formData.estimated_delivery_days || ''}
                onChange={(e) => handleChange('estimated_delivery_days', e.target.value)}
                type="number"
                placeholder="21"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Default delivery timeframe from Guangzhou</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>
    </div>
  );
};

export default SystemConfig;
